import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.eventListeners = new Map();
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  // Initialize socket connection
  connect(userType = 'admin') {
    if (this.socket && this.isConnected) {
      console.log('Socket already connected');
      return;
    }

    const serverUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    
    console.log('🔌 Connecting to Socket.IO server:', serverUrl);
    
    this.socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    // Handle connection
    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Authenticate with server
      this.authenticate(userType);
      
      this.emit('socket_connected');
    });

    // Handle authentication response
    this.socket.on('authenticated', (data) => {
      console.log('✅ Socket authenticated:', data);
      this.emit('socket_authenticated', data);
    });

    this.socket.on('auth_error', (error) => {
      console.error('❌ Socket authentication error:', error);
      this.emit('socket_auth_error', error);
    });

    // Handle new notifications
    this.socket.on('new_notification', (notification) => {
      console.log('📢 New notification received:', notification);
      this.emit('new_notification', notification);
    });

    // Handle system messages
    this.socket.on('system_message', (message) => {
      console.log('📢 System message received:', message);
      this.emit('system_message', message);
    });

    // Handle disconnection
    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
      this.isConnected = false;
      this.emit('socket_disconnected', reason);
      
      // Auto-reconnect logic
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't reconnect
        return;
      }
      
      this.handleReconnect(userType);
    });

    // Handle connection errors
    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      this.isConnected = false;
      this.emit('socket_error', error);
      
      this.handleReconnect(userType);
    });

    return this.socket;
  }

  // Authenticate with server
  authenticate(userType) {
    const token = userType === 'admin' 
      ? localStorage.getItem('adminToken')
      : localStorage.getItem('installerToken');
    
    if (!token) {
      console.error('❌ No authentication token found');
      return;
    }

    console.log('🔐 Authenticating socket with server...');
    this.socket.emit('authenticate', { token, userType });
  }

  // Handle reconnection
  handleReconnect(userType) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      this.emit('socket_max_reconnect_attempts');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    console.log(`🔄 Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      if (!this.isConnected) {
        this.connect(userType);
      }
    }, delay);
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.eventListeners.clear();
    }
  }

  // Send notification read acknowledgment
  markNotificationRead(notificationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('notification_read', { notificationId });
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in socket event listener:', error);
        }
      });
    }
  }

  // Get connection status
  isSocketConnected() {
    return this.isConnected && this.socket && this.socket.connected;
  }

  // Get socket ID
  getSocketId() {
    return this.socket ? this.socket.id : null;
  }
}

// Create singleton instance
const socketService = new SocketService();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  window.socketService = socketService;
}

export default socketService;
